/**
 * PostReviewApi - API service for post-level review operations
 *
 * Connects to backend endpoints:
 * - GET /api/admin/posts
 * - GET /api/admin/posts/{postId}/mentions
 *
 * Backend controller: Backend/MentionedMe.API/Controllers/PostReviewController.cs
 */
import { apiClient } from './ApiClient';
import type {
  PostSummaryDto,
  PostMentionGroupDto,
  GetPostsForReviewParams
} from '@/domain/entities/PostReview';
import type { PagedResult } from '@/domain/entities/PagedResult';

/**
 * Base path for post review API endpoints
 */
const BASE_PATH = '/api/admin/posts';

/**
 * PostReviewApi class
 *
 * Provides type-safe methods for interacting with post review API
 */
export class PostReviewApi {
  /**
   * Get paginated list of posts with aggregated mention statistics
   *
   * @param params - Query parameters (filters, pagination, sorting)
   * @returns Paged result of post summaries
   *
   * @example
   * const result = await postReviewApi.getPostsForReview({
   *   page: 1,
   *   pageSize: 20,
   *   subreddit: 'Seattle',
   *   reviewStatus: 'AllPending',
   *   sortBy: 'PostDate',
   *   sortDirection: 'Desc'
   * });
   */
  async getPostsForReview(
    params?: GetPostsForReviewParams
  ): Promise<PagedResult<PostSummaryDto>> {
    // Build query parameters
    const queryParams = new URLSearchParams();

    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.pageSize) {
      queryParams.append('pageSize', params.pageSize.toString());
    }
    if (params?.subreddit) {
      queryParams.append('subreddit', params.subreddit);
    }
    if (params?.reviewStatus) {
      queryParams.append('reviewStatus', params.reviewStatus);
    }
    if (params?.minMentionCount !== undefined && params.minMentionCount !== null) {
      queryParams.append('minMentionCount', params.minMentionCount.toString());
    }
    if (params?.startDate) {
      queryParams.append('startDate', params.startDate);
    }
    if (params?.endDate) {
      queryParams.append('endDate', params.endDate);
    }
    if (params?.algorithmVersion) {
      queryParams.append('algorithmVersion', params.algorithmVersion);
    }
    if (params?.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params?.sortDirection) {
      queryParams.append('sortDirection', params.sortDirection);
    }

    const url = `${BASE_PATH}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    return apiClient.get<PagedResult<PostSummaryDto>>(url);
  }

  /**
   * Get hierarchical view of a specific post with all its mentions
   *
   * @param postId - Reddit post ID (e.g., "t3_abc123")
   * @returns Post with hierarchical mention structure
   *
   * @example
   * const postDetail = await postReviewApi.getPostMentions('t3_1abc23x');
   * // Returns post with:
   * // - topLevelMentions: Mentions in the post itself
   * // - comments: Comments with their nested mentions
   */
  async getPostMentions(postId: string): Promise<PostMentionGroupDto> {
    const url = `${BASE_PATH}/${encodeURIComponent(postId)}/mentions`;
    return apiClient.get<PostMentionGroupDto>(url);
  }

  /**
   * Search for a post by its Reddit post ID
   *
   * @param postId - Reddit post ID (e.g., "t3_abc123")
   * @returns Post with mentions if found, null if not found
   *
   * @example
   * const result = await postReviewApi.searchByPostId('t3_1abc23x');
   * if (result) {
   *   console.log('Found:', result.postTitle);
   * } else {
   *   console.log('Post not found in database');
   * }
   */
  async searchByPostId(postId: string): Promise<PostMentionGroupDto | null> {
    try {
      const url = `${BASE_PATH}/${encodeURIComponent(postId)}/mentions`;
      return await apiClient.get<PostMentionGroupDto>(url);
    } catch (error) {
      // Handle 404 (not found) gracefully by returning null
      if (this.isNotFoundError(error)) {
        return null;
      }
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Type guard to check if an error is a 404 Not Found error
   */
  private isNotFoundError(error: unknown): boolean {
    // Check for axios-style error with response status
    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      typeof (error as Record<string, unknown>).response === 'object' &&
      (error as Record<string, unknown>).response !== null
    ) {
      const response = (error as { response: { status?: number } }).response;
      return response.status === 404;
    }
    return false;
  }

  /**
   * Create a manual mention for a business the algorithm missed
   *
   * @param postId - Reddit post ID (e.g., "t3_abc123")
   * @param request - Manual mention creation details
   * @returns Response with created mention and business IDs
   *
   * Sprint 044: Manual mention creation from post review
   */
  async createManualMention(
    postId: string,
    request: CreateManualMentionRequest
  ): Promise<CreateManualMentionResponse> {
    const url = `${BASE_PATH}/${encodeURIComponent(postId)}/create-manual-mention`;
    return apiClient.post<CreateManualMentionResponse>(url, request);
  }
}

/**
 * Request body for creating a manual mention
 * Sprint 044: Maps to Backend CreateManualMentionRequest DTO
 */
export interface CreateManualMentionRequest {
  // Post context
  contentSource: 'PostBody' | 'Comment';
  redditCommentId?: string;
  mentionText: string;
  adminNotes?: string;

  // Business resolution (mutually exclusive)
  businessId?: string; // Existing business

  // New business fields
  name?: string;
  category?: string;
  regionId?: string;
  email?: string;
  phone?: string;
  website?: string;

  // Address fields (optional, for new business)
  houseNumber?: string;
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  subAreaId?: string;

  /** Emotional tone for sentiment-aware email template selection */
  emotionalTone?: 'Positive' | 'Negative' | 'Neutral';
}

/**
 * Response from creating a manual mention
 * Sprint 044: Maps to Backend CreateManualMentionResponse
 */
export interface CreateManualMentionResponse {
  success: boolean;
  message: string;
  mentionId: string;
  businessId: string;
  businessWasCreated: boolean;
}

/**
 * Default post review API instance
 */
export const postReviewApi = new PostReviewApi();
